import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmployeePerformanceOverview } from "@/components/analytics/EmployeePerformanceOverview";
import { EmployeeRankingLeaderboard } from "@/components/analytics/EmployeeRankingLeaderboard";
import { EmployeeComparisonChart } from "@/components/analytics/EmployeeComparisonChart";
import { EmployeeMetricsGrid } from "@/components/analytics/EmployeeMetricsGrid";
import { PerformanceTrends } from "@/components/analytics/PerformanceTrends";
import { 
  BarChart3,
  TrendingUp, 
  Trophy,
  Users,
  Target,
  Activity,
  Zap,
  Award,
  Calendar,
  Filter
} from "lucide-react";

const EmployeeAnalytics = () => {
  const [timeFilter, setTimeFilter] = useState("30");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 p-6" dir="rtl">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800">تحليلات أداء الموظفين</h1>
              <p className="text-slate-600">قياس الكفاءة والأداء والمنافسة بين الموظفين</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Select value={timeFilter} onValueChange={setTimeFilter}>
              <SelectTrigger className="w-48">
                <Filter className="w-4 h-4 ml-2" />
                <SelectValue placeholder="فترة التحليل" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">آخر 7 أيام</SelectItem>
                <SelectItem value="30">آخر 30 يوم</SelectItem>
                <SelectItem value="90">آخر 3 أشهر</SelectItem>
                <SelectItem value="365">آخر سنة</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-48">
                <Users className="w-4 h-4 ml-2" />
                <SelectValue placeholder="القسم" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الأقسام</SelectItem>
                <SelectItem value="sales">المبيعات</SelectItem>
                <SelectItem value="customer_service">خدمة العملاء</SelectItem>
                <SelectItem value="operations">العمليات</SelectItem>
                <SelectItem value="marketing">التسويق</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {/* Performance Overview Cards */}
      <EmployeePerformanceOverview timeFilter={timeFilter} departmentFilter={departmentFilter} />

      {/* Main Analytics Tabs */}
      <Tabs defaultValue="leaderboard" className="mt-8">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="leaderboard" className="flex items-center gap-2">
            <Trophy className="w-4 h-4" />
            الترتيب العام
          </TabsTrigger>
          <TabsTrigger value="comparison" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            المقارنة
          </TabsTrigger>
          <TabsTrigger value="metrics" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            المقاييس
          </TabsTrigger>
          <TabsTrigger value="trends" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            الاتجاهات
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            التقييم الشامل
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leaderboard" className="mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <EmployeeRankingLeaderboard timeFilter={timeFilter} departmentFilter={departmentFilter} />
            </div>
            <div className="space-y-6">
              <Card className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Trophy className="w-5 h-5" />
                    موظف الشهر
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Trophy className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-bold text-xl">أحمد محمد</h3>
                    <p className="text-yellow-100">قسم المبيعات</p>
                    <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-yellow-100">المبيعات</p>
                        <p className="font-bold">150,000 ر.س</p>
                      </div>
                      <div>
                        <p className="text-yellow-100">الترتيب</p>
                        <p className="font-bold">#1</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg text-slate-800">
                    <Zap className="w-5 h-5" />
                    إحصائيات سريعة
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">أعلى مبيعات فردية</span>
                    <Badge className="bg-green-100 text-green-700">85,000 ر.س</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">متوسط الأداء</span>
                    <Badge className="bg-blue-100 text-blue-700">87%</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">أفضل قسم</span>
                    <Badge className="bg-purple-100 text-purple-700">المبيعات</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">معدل النمو</span>
                    <Badge className="bg-emerald-100 text-emerald-700">+23%</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="comparison" className="mt-8">
          <EmployeeComparisonChart timeFilter={timeFilter} departmentFilter={departmentFilter} />
        </TabsContent>

        <TabsContent value="metrics" className="mt-8">
          <EmployeeMetricsGrid timeFilter={timeFilter} departmentFilter={departmentFilter} />
        </TabsContent>

        <TabsContent value="trends" className="mt-8">
          <PerformanceTrends timeFilter={timeFilter} departmentFilter={departmentFilter} />
        </TabsContent>

        <TabsContent value="performance" className="mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  التقييم الشامل للأداء
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {[
                    { name: "أحمد محمد", score: 95, department: "المبيعات", badge: "ممتاز" },
                    { name: "فاطمة علي", score: 88, department: "خدمة العملاء", badge: "جيد جداً" },
                    { name: "محمد خالد", score: 82, department: "العمليات", badge: "جيد جداً" },
                    { name: "سارة أحمد", score: 75, department: "التسويق", badge: "جيد" },
                    { name: "علي حسن", score: 68, department: "المبيعات", badge: "مقبول" }
                  ].map((employee, index) => (
                    <div key={employee.name} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          index === 0 ? 'bg-yellow-500' : 
                          index === 1 ? 'bg-gray-400' : 
                          index === 2 ? 'bg-amber-600' : 'bg-slate-400'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{employee.name}</p>
                          <p className="text-sm text-slate-600">{employee.department}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-slate-800">{employee.score}</span>
                          <span className="text-slate-500">%</span>
                        </div>
                        <Badge className={`text-xs ${
                          employee.score >= 90 ? 'bg-green-100 text-green-700' :
                          employee.score >= 80 ? 'bg-blue-100 text-blue-700' :
                          employee.score >= 70 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {employee.badge}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  تقرير الأداء الشهري
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-700">23</div>
                      <div className="text-sm text-green-600">موظف متفوق</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-700">45</div>
                      <div className="text-sm text-blue-600">أداء جيد</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-700">12</div>
                      <div className="text-sm text-yellow-600">يحتاج تحسين</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-700">3</div>
                      <div className="text-sm text-red-600">أداء ضعيف</div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold text-slate-800 mb-3">توصيات التحسين</h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        <span>تقديم برامج تدريبية للموظفين ذوي الأداء المنخفض</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                        <span>نظام حوافز للموظفين المتفوقين</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                        <span>مراجعة دورية للأهداف والمقاييس</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployeeAnalytics;